import React from 'react';
import { fetchQuoteItemsFromServer, saveQuoteItemsToServer } from '../api/woocommerce/quote-items.api';
import { useAppSelector } from '../store/hooks';

export interface QuoteItem {
  id: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
}

interface QuoteContextValue {
  items: QuoteItem[];
  itemCount: number;
  addToQuote: (item: QuoteItem, options?: AddToQuoteOptions) => void;
  removeFromQuote: (id: number) => void;
  updateQuoteQuantity: (id: number, quantity: number) => void;
  clearQuote: () => void;
}

interface AddToQuoteOptions {
  source?: 'single-product' | 'listing' | 'other';
}

interface ToastMessage {
  id: number;
  title: string;
  actions: ToastAction[];
}

interface ToastAction {
  label: string;
  href?: string;
  variant?: 'primary' | 'secondary';
}

const QuoteContext = React.createContext<QuoteContextValue | undefined>(undefined);

const QUOTE_STORAGE_KEY = 'quote';

const normalizeQuoteItem = (item: unknown): QuoteItem | null => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const candidate = item as Partial<QuoteItem>;
  const id = Number(candidate.id);
  const quantity = Math.max(1, Number(candidate.quantity) || 1);
  const price = Number(candidate.price) || 0;

  if (!Number.isFinite(id) || !candidate.name) {
    return null;
  }

  return {
    id,
    name: String(candidate.name),
    price,
    image: typeof candidate.image === 'string' ? candidate.image : '',
    quantity,
  };
};

const normalizeQuoteItems = (items: unknown): QuoteItem[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => normalizeQuoteItem(item))
    .filter((item): item is QuoteItem => item !== null);
};

const getInitialQuoteItems = (): QuoteItem[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const persistedQuote = window.localStorage.getItem(QUOTE_STORAGE_KEY);

  if (!persistedQuote) {
    return [];
  }

  try {
    return normalizeQuoteItems(JSON.parse(persistedQuote));
  } catch {
    return [];
  }
};

const persistQuoteItems = (items: QuoteItem[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(QUOTE_STORAGE_KEY, JSON.stringify(items));
};

const recalculateItemCount = (items: QuoteItem[]): number =>
  items.reduce((count, item) => count + item.quantity, 0);

const upsertQuoteItem = (items: QuoteItem[], incomingItem: QuoteItem): QuoteItem[] => {
  const existingItem = items.find((item) => item.id === incomingItem.id);
  const normalizedIncomingItem = normalizeQuoteItem(incomingItem);

  if (!normalizedIncomingItem) {
    return items;
  }

  if (!existingItem) {
    return [...items, normalizedIncomingItem];
  }

  return items.map((item) =>
    item.id === normalizedIncomingItem.id
      ? {
          ...item,
          quantity: item.quantity + normalizedIncomingItem.quantity,
          name: normalizedIncomingItem.name || item.name,
          image: normalizedIncomingItem.image || item.image,
          price: Number.isFinite(normalizedIncomingItem.price) ? normalizedIncomingItem.price : item.price,
        }
      : item
  );
};

export const QuoteProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [items, setItems] = React.useState<QuoteItem[]>(() => getInitialQuoteItems());
  const [toasts, setToasts] = React.useState<ToastMessage[]>([]);
  const { isAuthenticated, token } = useAppSelector((state) => state.auth);

  const itemCount = React.useMemo(() => recalculateItemCount(items), [items]);

  const syncItemsToServer = React.useCallback(
    async (nextItems: QuoteItem[]) => {
      if (!isAuthenticated || !token) {
        return;
      }

      try {
        await saveQuoteItemsToServer(nextItems, token);
      } catch (error) {
        console.error('Failed to sync quote items to server:', error);
      }
    },
    [isAuthenticated, token]
  );

  React.useEffect(() => {
    let isActive = true;

    const hydrateQuoteItems = async (): Promise<void> => {
      const localItems = getInitialQuoteItems();

      if (!isAuthenticated || !token) {
        if (isActive) {
          setItems(localItems);
        }
        return;
      }

      try {
        const serverItems = normalizeQuoteItems(await fetchQuoteItemsFromServer(token));

        if (serverItems.length > 0) {
          if (isActive) {
            setItems(serverItems);
            persistQuoteItems(serverItems);
          }
          return;
        }

        if (localItems.length > 0) {
          await saveQuoteItemsToServer(localItems, token);
        }

        if (isActive) {
          setItems(localItems);
          persistQuoteItems(localItems);
        }
      } catch (error) {
        console.error('Failed to hydrate quote items from server:', error);

        if (isActive) {
          setItems(localItems);
        }
      }
    };

    void hydrateQuoteItems();

    return () => {
      isActive = false;
    };
  }, [isAuthenticated, token]);

  React.useEffect(() => {
    const handleStorage = (event: StorageEvent): void => {
      if (event.key !== QUOTE_STORAGE_KEY) {
        return;
      }

      if (!event.newValue) {
        setItems([]);
        return;
      }

      try {
        setItems(normalizeQuoteItems(JSON.parse(event.newValue)));
      } catch {
        setItems([]);
      }
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const dismissToast = React.useCallback((toastId: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== toastId));
  }, []);

  const showToast = React.useCallback(
    (title: string, actions: ToastAction[]) => {
      const toastId = Date.now() + Math.floor(Math.random() * 1000);

      setToasts((current) => [...current, { id: toastId, title, actions }]);

      window.setTimeout(() => {
        dismissToast(toastId);
      }, 4500);
    },
    [dismissToast]
  );

  const addToQuote = React.useCallback(
    (item: QuoteItem, options?: AddToQuoteOptions) => {
      setItems((currentItems) => {
        const updatedItems = upsertQuoteItem(currentItems, item);
        persistQuoteItems(updatedItems);
        void syncItemsToServer(updatedItems);
        return updatedItems;
      });

      const toastActions: ToastAction[] = [
        {
          label: 'View Quote List',
          href: '/quotation',
          variant: 'primary',
        },
      ];

      if (options?.source === 'single-product') {
        toastActions.unshift({ label: 'Continue Shopping', variant: 'secondary' });
      }

      showToast('Added to Quote Request', toastActions);
    },
    [showToast, syncItemsToServer]
  );

  const removeFromQuote = React.useCallback((id: number) => {
    setItems((currentItems) => {
      const updatedItems = currentItems.filter((item) => item.id !== id);
      persistQuoteItems(updatedItems);
      void syncItemsToServer(updatedItems);
      return updatedItems;
    });
  }, [syncItemsToServer]);

  const updateQuoteQuantity = React.useCallback((id: number, quantity: number) => {
    setItems((currentItems) => {
      const updatedItems = currentItems
        .map((item) =>
          item.id === id
            ? {
                ...item,
                quantity: Number.isFinite(quantity) ? Math.max(0, Math.floor(quantity)) : item.quantity,
              }
            : item
        )
        .filter((item) => item.quantity > 0);

      persistQuoteItems(updatedItems);
      void syncItemsToServer(updatedItems);
      return updatedItems;
    });
  }, [syncItemsToServer]);

  const clearQuote = React.useCallback(() => {
    setItems([]);
    persistQuoteItems([]);
    void syncItemsToServer([]);
  }, [syncItemsToServer]);

  const contextValue = React.useMemo(
    () => ({
      items,
      itemCount,
      addToQuote,
      removeFromQuote,
      updateQuoteQuantity,
      clearQuote,
    }),
    [addToQuote, clearQuote, itemCount, items, removeFromQuote, updateQuoteQuantity]
  );

  return (
    <QuoteContext.Provider value={contextValue}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-[9999] flex w-full max-w-xs flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="pointer-events-auto rounded-xl border border-purple-400/50 bg-slate-900/90 px-4 py-3 text-sm font-semibold text-purple-100 shadow-lg shadow-purple-500/20 backdrop-blur-md"
          >
            <p>{toast.title}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {toast.actions.map((action) => {
                const actionClasses =
                  action.variant === 'primary'
                    ? 'bg-purple-500 text-white hover:bg-purple-400'
                    : 'border border-slate-500 text-slate-200 hover:border-slate-300 hover:text-white';

                if (action.href) {
                  return (
                    <a
                      key={action.label}
                      href={action.href}
                      onClick={() => dismissToast(toast.id)}
                      className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${actionClasses}`}
                    >
                      {action.label}
                    </a>
                  );
                }

                return (
                  <button
                    key={action.label}
                    type="button"
                    onClick={() => dismissToast(toast.id)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${actionClasses}`}
                  >
                    {action.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </QuoteContext.Provider>
  );
};

export const useQuote = (): QuoteContextValue => {
  const context = React.useContext(QuoteContext);

  if (!context) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }

  return context;
};
